import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Thông báo trong ứng dụng (P05.7). Logic chọn người nhận, nội dung nằm ở server; web chỉ hiển thị. */
export type Priority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
/** Mức theo người nhận (anh Tiến 21/10/2026): Khẩn, Trong ngày (chuông, số đỏ) / Thông tin (Bản tin, không số đỏ). */
export type NotifyLevel = 'urgent' | 'today' | 'info';
export type NotifyBox = 'action' | 'info';
export interface AppNotification {
  id: number;
  eventId: string;
  eventType: string;
  type: string;
  priority: Priority;
  title: string;
  message: string | null;
  entityType: string | null;
  entityId: string | null;
  link: string | null;
  metadata: Record<string, unknown>;
  level: NotifyLevel;
  baseLevel: NotifyLevel;
  audience: 'doer' | 'head' | 'exec';
  area: string;
  escalatedAt: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  expiresAt: string;
}
export interface NotificationPage {
  items: AppNotification[];
  nextCursor: number | null;
  hasMore: boolean;
  unreadCount: number;
  infoUnreadCount: number;
}
export interface NotificationFilter {
  cursor?: number;
  limit?: number;
  unreadOnly?: boolean;
  type?: string;
  priority?: Priority;
  box?: NotifyBox;
}

export { NOTIFY_LEVEL_LABELS as LEVEL_LABELS } from '@mt/shared';
export const LEVEL_BADGE: Record<NotifyLevel, 'neutral' | 'warning' | 'error'> = { urgent: 'error', today: 'warning', info: 'neutral' };
export const PRIORITY_LABELS: Record<Priority, string> = { LOW: 'Thấp', NORMAL: 'Bình thường', HIGH: 'Cao', URGENT: 'Khẩn' };
export const PRIORITY_BADGE: Record<Priority, 'neutral' | 'info' | 'warning' | 'error'> = { LOW: 'neutral', NORMAL: 'info', HIGH: 'warning', URGENT: 'error' };
export const TYPE_LABELS: Record<string, string> = {
  LEAD_ASSIGNED: 'Giao lead',
  OPPORTUNITY_CREATED: 'Cơ hội mới',
  OPPORTUNITY_WON: 'Cơ hội thắng',
  OPPORTUNITY_LOST: 'Cơ hội thua',
  OPPORTUNITY_ASSIGNED: 'Giao theo dõi cơ hội',
  COMPLAINT_ASSIGNED: 'Khiếu nại giao cho tôi',
  COMPLAINT_RESOLVED: 'Khiếu nại đã xử lý',
  COMPLAINT_OVERDUE: 'Khiếu nại quá hạn',
  QUOTE_SENT: 'Báo giá đã gửi',
  QUOTE_ACCEPTED: 'Báo giá chốt',
  QUOTE_LOST: 'Báo giá không thành công',
  QUOTE_EXPIRING: 'Báo giá sắp hết hạn',
  QUOTE_EXTENDED: 'Báo giá gia hạn',
  ASIA_SYNC_FAILED: 'Đồng bộ Asia lỗi',
  ASIA_SYNC_ERRORS: 'Đồng bộ Asia có dòng lỗi',
  FOLLOW_UP_DUE: 'Việc cần làm',
  NOTIFY_DIGEST: 'Tóm tắt Bản tin cuối ngày',
  EXEC_DIGEST: 'Bản tin giám sát 09:00 / 15:30 (Giám đốc)',
  SYSTEM_EVENT_FAILED: 'Sự kiện lỗi',
  SYSTEM_DISK_LOW: 'Đĩa máy chủ sắp đầy',
  PRICE_LIST_CHANGED: 'Bảng giá đổi',
  APPROVAL_REQUESTED: 'Chờ tôi duyệt',
  APPROVAL_APPROVED: 'Báo giá đã duyệt',
  APPROVAL_REJECTED: 'Báo giá bị từ chối duyệt',
  WORKFLOW_TASK: 'Hộp duyệt: việc chờ tôi duyệt',
  WORKFLOW_RESULT: 'Hộp duyệt: kết quả duyệt',
  WORKFLOW_OVERDUE: 'Hộp duyệt: việc duyệt quá hạn',
  ESIGN_TASK: 'Ký điện tử: đến lượt tôi ký',
  ESIGN_RESULT: 'Ký điện tử: kết quả hồ sơ tôi trình',
  POSTER_ORDER: 'Khách đặt hàng từ poster',
  WEB_QUOTE_REQUEST: 'Khách gửi Báo giá nhanh trên web',
  CONTRACT_SIGNED: 'Khách đã ký hợp đồng',
  DEBT_CONFIRMATION_RESPONDED: 'Khách trả lời đối chiếu công nợ',
  FLEET_EXPIRING: 'Giấy tờ xe, bằng lái sắp hết hạn',
  FLEET_FINE_CHECK: 'Nhắc kiểm phạt nguội thứ Hai',
  FLEET_WEEKLY_CHECK: 'Kiểm xe thứ Hai (dầu, nước, phanh, lốp, cẩu)',
  FLEET_YARD_CHECK: 'Xe chưa về bãi (17:30, 21:00, 22:00, 24:00, 04:00)',
  FLEET_YARD_REPORT: 'Tổng hợp xe về bãi ngày 15 và cuối tháng',
  FLEET_FINE: 'Xe bị ghi phạt nguội',
  FLEET_FUEL_PENDING: 'Phiếu đổ dầu chờ duyệt',
  FLEET_CLOSEOUT_DUE: 'Nhắc chốt kỳ dầu cuối tháng',
  FLEET_FUEL_WEEKLY: 'Báo cáo dầu thứ Hai',
  FLEET_MONTH_REPORT: 'Báo cáo hiệu quả dầu tháng (sau chốt kỳ)',
  FLEET_ODO_ANOMALY: 'Km đồng hồ lệch định vị bất thường',
  FLEET_GPS_LONG_STOP: 'Xe đứng lâu ngoài bãi, kho, điểm giao',
  FLEET_GPS_REPORT: 'Báo cáo trạng thái xe theo khung giờ',
  DELIVERY_PICK: 'Phiếu soạn hàng cho kho',
  DELIVERY_GOODS_ARRIVED: 'Hàng chờ về đã về cho phiếu giao',
  ORDER_INCOMING_GOODS: 'Kinh doanh ghi hàng đang về trên đơn',
  DRIVER_DAY_BRIEF: 'Tài xế: đầu giờ "Đơn hàng hôm nay gồm…"',
  DELIVERY_OVERDUE: 'Phiếu giao quá ngày chưa giao xong (điều phối)',
  DELIVERY_AUTO_ASSIGNED: 'Phiếu đủ hàng đã tự gắn xe',
  DELIVERY_UNASSIGNED: 'Phiếu đủ hàng chưa có xe vừa tải',
  DELIVERY_PICK_SHORT: 'Kho báo thiếu hàng khi soạn',
  DELIVERY_PRINT_REQUEST: 'Kho nhờ in phiếu soạn hàng',
  DELIVERY_CONTACT_MISSING: 'Đơn đã SO3 còn thiếu địa chỉ giao / người nhận / SĐT',
  SO3_DOCS_INCOMPLETE: 'Phiếu SO3 ngày trước chưa đủ hồ sơ (nhắc 08:00, 14:00)',
  DELIVERY_ENOUGH_NEEDED: 'Phiếu từng thiếu hàng: kho đã đủ, cần xác nhận đủ',
  DELIVERY_ENOUGH_CONFIRMED: 'Phiếu đủ 3 xác nhận đủ hàng – gắn xe đi giao',
  DELIVERY_ASSIGNED: 'Tài xế được giao phiếu giao hàng',
  DELIVERY_VEHICLE_CHANGED: 'Phiếu giao đổi xe (tài xế cũ, kho soạn hàng)',
  DELIVERY_TRIP_STARTED: 'Chuyến của tài xế đã cho xe chạy',
  DELIVERY_DISPATCHED: 'Phiếu giao đã xuất giao',
  STOCK_TRANSFER_IN: 'Hàng chuyển kho đến kho của tôi',
  STOCK_COUNT_SUBMITTED: 'Kết quả kiểm kho chờ duyệt',
  STOCK_COUNT_REQUESTED: 'Cấp trên giao kiểm kho',
  STOCK_COUNT_PROPOSED: 'Đề xuất kiểm kho (tồn âm)',
  STOCK_COUNT_APPROVED: 'Kết quả kiểm kho đã duyệt',
  CONTROL_CHECK_REQUESTED: 'Đơn khách chốt chờ Kiểm soát đối chiếu',
  CONTROL_CHECK_MISMATCH: 'Kiểm soát báo đơn lệch, cần khách chốt lại',
  ASIA_SO1_CHANGED: 'Phiếu SO1 bị sửa trên Asia, cần khách chốt lại',
  TEMP_CODE_NEEDED: 'Mã tạm cần tạo trên Asia',
  PURCHASE_SHORTAGE: 'Đơn thiếu hàng: đề nghị mua phần thiếu',
  PURCHASE_RFQ_READY: 'Đợt hỏi giá NCC đã đủ giá, chờ chọn NCC / lên đơn',
  SUPPLIER_PORTAL_MESSAGE: 'NCC nhắn / báo giá trên Cổng NCC',
  FLEET_TRIP_OVERNIGHT: 'Tài xế ở lại qua đêm khi lấy hàng',
  FLEET_PICKUP_ASSIGNED: 'Được giao chuyến lấy hàng',
  FLEET_PICKUP_PROGRESS: 'Tiến độ chuyến lấy hàng (nhận, tới, lấy xong, về kho)',
  FLEET_PICKUP_CARGO: 'Hàng lấy về: chọn xuống kho / để trên xe / sang xe, kho xuống hàng',
  CARRIER_WALLET_LOW: 'Ví Lalamove sắp hết tiền (số dư ước tính dưới ngưỡng)',
  CARRIER_NO_DRIVER: 'Lalamove không có tài xế nhận (hết hạn, từ chối, tìm quá 15 phút)',
  FLEET_FUEL_VARIANCE: 'Hao dầu, lệch km định vị bất thường',
  FLEET_FUEL_PAY: 'Phiếu đổ dầu đã duyệt: chuyển tiền / duyệt chi',
  FLEET_EXPENSE_PENDING: 'Phiếu chi phí xe chờ duyệt',
  FLEET_TIRE_ALERT: 'Vỏ xe: sai mã khi kiểm, vỏ cũ quá hạn chưa nộp kho',
  FLEET_CONDITION: 'Khai báo tình trạng xe (lốp, lọc gió, lọc nhớt): đối chiếu, khóa, khai lại',
  FLEET_CHECKLIST_LOCKED: 'Khóa xe do checklist nhận xe không đạt',
  FLEET_SERVICE_REQUEST: 'Tài xế yêu cầu bảo dưỡng xe: chờ duyệt, đã duyệt, bảo dưỡng xong',
  FLEET_RETURN_REMINDER: 'Nhắc tài xế trả xe',
  FLEET_CRANE_OPEN: 'Nhắc tài xế bấm Xong cẩu (đang cẩu quá 60 phút)',
  FLEET_AUTO_RETURN: 'Trả xe tự động / xin sửa giờ',
  FLEET_TRIP_FLAGGED: 'Chuyến lấy hàng có cờ đỏ',
  FLEET_MAINTENANCE_DUE: 'Xe sắp tới hạn / quá hạn bảo dưỡng',
  FLEET_TASK_ASSIGNED: 'Việc được giao cho tài xế',
  FLEET_TASK_DONE: 'Tài xế báo xong việc',
  HR_LEAVE_REQUESTED: 'Đơn xin nghỉ chờ duyệt',
  HR_LEAVE_DECIDED: 'Kết quả đơn xin nghỉ',
  HR_ADVANCE_REQUESTED: 'Đề nghị tạm ứng chờ xử lý',
  HR_ADVANCE_DECIDED: 'Kết quả đề nghị tạm ứng',
  HR_CONTRACT_EXPIRING: 'Hợp đồng lao động sắp hết hạn',
  HR_PROBATION_ENDING: 'Sắp hết thử việc, cần đánh giá',
  HR_CONTRACT_SIGN: 'Hợp đồng lao động chờ ký trên ứng dụng',
  HR_POLICY_PUBLISHED: 'Nội quy, quy định mới',
  HR_WORK_ORDER: 'Lệnh tăng ca, điều động, giao việc',
  HR_VIOLATION: 'Vi phạm chấm công, giải trình',
  HR_PRIVATE_CHANGED: 'NV tự đổi số CCCD, tài khoản nhận lương',
  HR_HEALTH_DOC_EXPIRING: 'Sắp tới kỳ khám sức khoẻ, thẻ BHYT / BHNT sắp hết hạn',
  HR_TIMESHEET_CONFIRM: 'Xác nhận bảng công tháng',
  HR_CHECKIN_MISSING: 'Bản tin sáng 07:30: chưa chấm công, đi trễ, việc tồn',
  HR_PAYOUT_RECON: 'Đối soát chi lương: chưa chi, lệch chưa xử lý',
  HR_PIECE_PAYMENT: 'Đợt thanh toán đội khoán',
  SITE_PROGRESS: 'Tiến độ công trình',
  HR_TRAINING: 'Đào tạo: khóa học, lớp học',
  HR_KPI: 'KPI: tự đánh giá, TP chấm, tăng lương',
  HR_PAYROLL_RECOVERY: 'Thu hồi chi dư trừ vào lương',
  DAILY_SUMMARY: 'Tóm tắt công việc hằng ngày',
  WEB_CHAT_NEW: 'Khách chat trên web PCCC',
  SITE_REPORTED: 'Dự án công trình mới được báo',
  SITE_UPDATED: 'Dự án bạn báo có tiến triển',
  FEED_POST: 'Bảng tin: bài mới, thông báo công ty',
  FEED_COMMENT: 'Bảng tin: bình luận, trả lời',
  FEED_REACTION: 'Bảng tin: Thích, Tim',
  FEED_BIRTHDAY: 'Sinh nhật đồng nghiệp hôm nay',
  LUCKY_WHEEL_WIN: 'Vòng quay may mắn: trúng tiền, quà',
  CTV_COMMISSION: 'Đề xuất % hoa hồng CTV, kết quả duyệt',
  QUOTE_REF_COST: 'Giá nhập tham khảo chờ duyệt, kết quả duyệt',
  TENDER_DEADLINE: 'Dự thầu: sắp hết hạn làm rõ HSMT, đóng thầu, bảo lãnh',
  WEB_POST: 'Đăng bài web: bài chờ duyệt, kết quả duyệt / gỡ',
};

export const NOTIF_KEY = ['notifications'] as const;

export const useNotificationList = (f: NotificationFilter, enabled = true) =>
  useQuery({ queryKey: [...NOTIF_KEY, 'list', f], queryFn: () => api.get<NotificationPage>('/notifications', { ...f }), enabled });

export const fetchNotificationsAfter = (after: number) => api.get<NotificationPage>('/notifications', { after, limit: 100 });
export const fetchUnreadCount = () => api.get<{ count: number; infoCount: number }>('/notifications/unread-count');
export const markNotificationRead = (id: number) => api.post<{ ok: true; unreadCount: number; infoUnreadCount: number }>(`/notifications/${id}/read`);
export const markAllNotificationsRead = (box?: NotifyBox) =>
  api.post<{ updated: number; unreadCount: number; infoUnreadCount: number }>('/notifications/read-all', box ? { box } : {});

// ---------------------------------------------------------------- Sự kiện lỗi (notification.admin)
export interface ProblemEvent {
  id: number;
  eventId: string;
  eventType: string;
  entityType: string;
  entityId: string;
  status: 'pending' | 'sent' | 'failed' | 'dlq';
  attemptCount: number;
  lastError: string | null;
  correlationId: string | null;
  occurredAt: string;
  nextAttemptAt: string;
  actorName: string | null;
}

export const useProblemEvents = (status: 'dlq' | 'failed', page: number) =>
  useQuery({
    queryKey: ['admin-events', status, page],
    queryFn: () => api.get<{ items: ProblemEvent[]; total: number; page: number; pageSize: number }>('/admin/events', { status, page, pageSize: 50 }),
  });

export function useRetryEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ProblemEvent>(`/admin/events/${id}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-events'] }),
  });
}

// ---------------------------------------------------------------- Đĩa máy chủ (notification.admin)
export interface DiskStatus {
  path: string;
  totalBytes: number;
  freeBytes: number;
  usedBytes: number;
  freePercent: number;
  level: 'ok' | 'warn' | 'critical';
  warnBelowPercent: number;
  criticalBelowPercent: number;
  checkedAt: string;
}

export const useDiskStatus = () =>
  useQuery({ queryKey: ['admin-disk'], queryFn: () => api.get<DiskStatus>('/admin/system/disk'), refetchInterval: 5 * 60_000 });
